import { useMemo } from "react";

import {
  KANBAN_COLORS_DARK,
  KANBAN_COLORS_LIGHT,
  type KanbanColors,
} from "../constants/colors";

import { useTheme } from "@/providers/ThemeProvider";

/**
 * Hook to get theme-aware colors for the Kanban board
 *
 * This hook returns the appropriate color palette based on the current app theme.
 * It follows the global theme setting from ThemeProvider.
 * @returns Theme-appropriate color palette for Kanban board components
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const colors = useKanbanColors();
 *   const boxStyle = { backgroundColor: colors.mainBackground };
 *   const textStyle = { color: colors.textPrimary };
 *
 *   return (
 *     <View style={boxStyle}>
 *       <Text style={textStyle}>Hello</Text>
 *     </View>
 *   );
 * };
 * ```
 */
export function useKanbanColors(): KanbanColors {
  const { resolvedTheme } = useTheme();

  return useMemo(() => {
    return resolvedTheme === "dark" ? KANBAN_COLORS_DARK : KANBAN_COLORS_LIGHT;
  }, [resolvedTheme]);
}
